import { c as _c } from "react/compiler-runtime";
import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { $downloadStreamedFile } from '@/modules/files/utils/fileUtils';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useCaptureElementAsPng } from '@/utils/printUtils';
import { BALANCE_SHEET_TABLE_CLASS_NAME } from '../BalanceSheetTable/BalanceSheetTable.constants';
import { useBalanceSheetTableContext } from '../BalanceSheetTable/BalanceSheetTableContext';
import { DownloadBalanceSheetExcelFileV2Mutation, useDownloadBalanceSheetExcelFileV2Mutation } from './graphql/BalanceSheetExportDropdown.generated';
enum ExportFormat {
  EXCEL = 'EXCEL',
  PNG = 'PNG',
}
interface BalanceSheetExportDropdownProps {
  disabled: boolean;
}
export function BalanceSheetExportDropdown(t0) {
  const $ = _c(30);
  const {
    disabled
  } = t0;
  const trackUserEvent = useTrackUserEvent();
  const {
    setRenderWhiteLabeled
  } = useBalanceSheetTableContext();
  const {
    householdId,
    displayName
  } = useHouseholdDetailsContext();
  const captureElementAsPng = useCaptureElementAsPng();
  const {
    showFeedback
  } = useFeedback();
  const {
    reportError
  } = useReportError();
  let t1;
  if ($[0] !== showFeedback) {
    t1 = async data => {
      const token = data.downloadBalanceSheetExcelV2?.token;
      if (token) {
        ;
        try {
          await $downloadStreamedFile(`/api/download/file?token=${token}`);
        } catch (t2) {
          showFeedback("Failed to generate Excel file. Please try again.");
        }
      } else {
        showFeedback("Failed to generate Excel file. Please try again.");
      }
    };
    $[0] = showFeedback;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== reportError || $[3] !== showFeedback) {
    t2 = error => {
      reportError("Error downloading balance sheet excel file", error);
      showFeedback("An error occurred while generating the Excel file. Please try again.");
    };
    $[2] = reportError;
    $[3] = showFeedback;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      onCompleted: t1,
      onError: t2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const [downloadBalanceSheetExcelFile, t4] = useDownloadBalanceSheetExcelFileV2Mutation(t3);
  const {
    loading
  } = t4;
  let t5;
  if ($[8] !== captureElementAsPng || $[9] !== displayName || $[10] !== setRenderWhiteLabeled) {
    t5 = async () => {
      setRenderWhiteLabeled(true);
      const tableEl = document.querySelector(`.${BALANCE_SHEET_TABLE_CLASS_NAME}`);
      if (tableEl) {
        tableEl.scrollLeft = 0;
      }
      await new Promise(_temp);
      const element = document.querySelector(`.${BALANCE_SHEET_TABLE_CLASS_NAME}`);
      setRenderWhiteLabeled(false);
      if (!element) {
        throw new Error("no element found");
      }
      const width = element.scrollWidth;
      const height = element.scrollHeight;
      captureElementAsPng(element as HTMLElement, `${displayName} Balance Sheet`, {
        width,
        height
      });
    };
    $[8] = captureElementAsPng;
    $[9] = displayName;
    $[10] = setRenderWhiteLabeled;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleExportPng = t5;
  let t6;
  if ($[12] !== downloadBalanceSheetExcelFile || $[13] !== handleExportPng || $[14] !== householdId || $[15] !== trackUserEvent) {
    t6 = async format => {
      if (!householdId) {
        throw new Error("Household ID is not set");
      }
      trackUserEvent("balance_sheet exported", {
        format
      });
      if (format === ExportFormat.EXCEL) {
        downloadBalanceSheetExcelFile({
          variables: {
            householdID: householdId
          }
        });
      } else {
        await handleExportPng();
      }
    };
    $[12] = downloadBalanceSheetExcelFile;
    $[13] = handleExportPng;
    $[14] = householdId;
    $[15] = trackUserEvent;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const handleExport = t6;
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      anchorOrigin: {
        horizontal: "left",
        vertical: "bottom"
      },
      transformOrigin: {
        horizontal: "left",
        vertical: 0
      }
    };
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== handleExport) {
    t8 = () => handleExport(ExportFormat.EXCEL);
    $[18] = handleExport;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== loading || $[21] !== t8) {
    t9 = <MenuItem loading={loading} onClick={t8} label="Export as Excel" />;
    $[20] = loading;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== handleExport) {
    t10 = <MenuItem onClick={() => handleExport(ExportFormat.PNG)} label="Export as PNG" />;
    $[23] = handleExport;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== disabled || $[26] !== loading || $[27] !== t10 || $[28] !== t9) {
    t11 = <ButtonWithPopover variant="secondary" label="Export" size="sm" loading={loading} disabled={disabled} popperProps={t7}>{t9}{t10}</ButtonWithPopover>;
    $[25] = disabled;
    $[26] = loading;
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}
function _temp(resolve) {
  return setTimeout(resolve, 10);
}